import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me } from '@wo/shared';
import { api, errorText, qs } from '../lib/api';
import { useMechanics, useSettings } from '../lib/hooks';
import type { CustomerRow, MachineRow, WorkOrderRow } from '../lib/types';
import { CustomerForm, MachineForm } from '../components/forms';
import { Button, Card, ErrorText, Field, Input, PageHeader, Select, Textarea } from '../components/ui';

export function NewWorkOrderPage({ me }: { me: Me }) {
  const settings = useSettings();
  const canAssign = me.role === 'ADMIN' || me.role === 'MANAGER';
  const mechanics = useMechanics(canAssign);
  const qc = useQueryClient();

  const [customer, setCustomer] = useState<CustomerRow | null>(null);
  const [customerQ, setCustomerQ] = useState('');
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [machine, setMachine] = useState<MachineRow | null>(null);
  const [serialQ, setSerialQ] = useState('');
  const [addingMachine, setAddingMachine] = useState(false);
  const [f, setF] = useState({ problemDescription: '', jobType: '', billingType: '', customerPo: '', machineLocation: '', assignToUserId: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<WorkOrderRow | null>(null);

  const customers = useQuery({
    queryKey: ['customers', customerQ],
    queryFn: () => api.get<CustomerRow[]>(`/api/customers${qs({ q: customerQ })}`),
    enabled: !customer && customerQ.trim().length >= 2,
  });
  const machines = useQuery({
    queryKey: ['machines', customer?.id, serialQ],
    queryFn: () => api.get<MachineRow[]>(`/api/machines${qs(serialQ ? { q: serialQ } : { customerId: customer!.id })}`),
    enabled: Boolean(customer) && !machine,
  });

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      const wo = await api.post<WorkOrderRow>('/api/work-orders', {
        customerId: customer!.id,
        machineId: machine!.id,
        ...f,
        assignToUserId: f.assignToUserId ? Number(f.assignToUserId) : null,
      });
      await qc.invalidateQueries({ queryKey: ['board'] });
      setCreated(wo);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <Card className="mx-auto max-w-lg text-center">
        <p className="text-slate-600">Work order created</p>
        <p className="my-4 text-6xl font-black text-blue-800">#{created.number}</p>
        <div className="flex justify-center gap-2">
          <Link to={`/work-orders/${created.id}`}><Button>Open it</Button></Link>
          <Button variant="secondary" onClick={() => window.location.reload()}>Start another</Button>
        </div>
      </Card>
    );
  }

  const ready = customer && machine && f.problemDescription.trim() && f.jobType;

  return (
    <>
      <PageHeader title="New Work Order" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="1. Customer">
          {customer ? (
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">{customer.name}</div>
                <div className="text-sm text-slate-600">{customer.phone}</div>
              </div>
              <Button variant="ghost" onClick={() => { setCustomer(null); setMachine(null); }}>Change</Button>
            </div>
          ) : addingCustomer ? (
            <CustomerForm onSaved={(c) => { setCustomer(c); setAddingCustomer(false); }} onCancel={() => setAddingCustomer(false)} />
          ) : (
            <div className="space-y-2">
              <Input placeholder="Search name or phone…" value={customerQ} onChange={(e) => setCustomerQ(e.target.value)} autoFocus />
              <ul className="divide-y rounded-md border">
                {customers.data?.map((c) => (
                  <li key={c.id}>
                    <button className="w-full px-3 py-2 text-left hover:bg-slate-50" onClick={() => setCustomer(c)}>
                      <span className="font-medium">{c.name}</span> <span className="text-sm text-slate-500">{c.phone}</span>
                    </button>
                  </li>
                ))}
                {customers.data?.length === 0 && <li className="px-3 py-2 text-sm text-slate-500">No match.</li>}
              </ul>
              <Button variant="secondary" onClick={() => setAddingCustomer(true)}>+ New customer</Button>
            </div>
          )}
        </Card>

        <Card title="2. Machine">
          {!customer ? (
            <p className="text-sm text-slate-500">Choose the customer first.</p>
          ) : machine ? (
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">{machine.make} {machine.model}</div>
                <div className="text-sm text-slate-600">S/N {machine.serialNumber}{machine.equipmentNo && ` · EQ# ${machine.equipmentNo}`}</div>
              </div>
              <Button variant="ghost" onClick={() => setMachine(null)}>Change</Button>
            </div>
          ) : addingMachine ? (
            <MachineForm customerId={customer.id} onSaved={(m) => { setMachine(m); setAddingMachine(false); }} onCancel={() => setAddingMachine(false)} />
          ) : (
            <div className="space-y-2">
              <Input placeholder="Search any serial number (blank = this customer's machines)" value={serialQ} onChange={(e) => setSerialQ(e.target.value)} />
              <ul className="divide-y rounded-md border">
                {machines.data?.map((m) => (
                  <li key={m.id}>
                    <button className="w-full px-3 py-2 text-left hover:bg-slate-50" onClick={() => setMachine(m)}>
                      <span className="font-medium">{m.make} {m.model}</span> <span className="text-sm text-slate-500">S/N {m.serialNumber}</span>
                      {m.customer && m.customer.id !== customer.id && <span className="ml-2 text-xs text-amber-700">({m.customer.name})</span>}
                      {m.isRentalFleet && <span className="ml-2 text-xs text-slate-500">rental fleet</span>}
                    </button>
                  </li>
                ))}
                {machines.data?.length === 0 && <li className="px-3 py-2 text-sm text-slate-500">No machines found.</li>}
              </ul>
              <Button variant="secondary" onClick={() => setAddingMachine(true)}>+ New machine</Button>
            </div>
          )}
        </Card>

        <Card title="3. The job" className="lg:col-span-2">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <Field label="Details of problem" required hint="Voice dictation arrives with the mechanic app (Phase 2).">
                <Textarea rows={4} value={f.problemDescription} onChange={set('problemDescription')} />
              </Field>
            </div>
            <Field label="Job type" required>
              <Select value={f.jobType} onChange={set('jobType')}>
                <option value="">Choose…</option>
                {settings.data?.jobTypes.map((t) => <option key={t}>{t}</option>)}
              </Select>
            </Field>
            <Field label="Billing (office use)">
              <Select value={f.billingType} onChange={set('billingType')}>
                <option value="">—</option>
                {settings.data?.billingTypes.map((b) => <option key={b.code} value={b.code}>{b.code} — {b.label}</option>)}
              </Select>
            </Field>
            <Field label="Customer P/O"><Input value={f.customerPo} onChange={set('customerPo')} /></Field>
            <Field label="Machine location"><Input value={f.machineLocation} onChange={set('machineLocation')} /></Field>
            {canAssign && (
              <Field label="Assign to (optional)">
                <Select value={f.assignToUserId} onChange={set('assignToUserId')}>
                  <option value="">Leave unassigned</option>
                  {mechanics.data?.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </Select>
              </Field>
            )}
          </div>
          <div className="mt-4 space-y-2">
            <ErrorText error={error} />
            <div className="flex justify-end">
              <Button onClick={save} disabled={!ready || saving}>{saving ? 'Saving…' : 'Save work order'}</Button>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
